import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMutation } from "@tanstack/react-query"
import { Input } from "@/src/components/ui/input"
import { Button } from "@/src/components/ui/button"
import type React from "react"
import { useState } from "react"
import { authClient } from "@/src/lib/auth-client"
import { useTranslation } from "@/src/i18n/client"

export const Route = createFileRoute('/_auth/reset-password')({
  component: ResetPasswordPage,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      token: (search.token as string) || '',
    }
  },
})

function ResetPasswordPage() {
  const { t } = useTranslation('auth')
  const { token } = Route.useSearch()
  const navigate = useNavigate()
  const [form, setForm] = useState({ password: "", confirmPassword: "" })
  const [error, setError] = useState("")
  const [success, setSuccess] = useState(false)

  const resetPasswordMutation = useMutation({
    mutationFn: async ({ password, token }: { password: string; token: string }) => {
      const result = await authClient.resetPassword({
        newPassword: password,
        token,
      })
      if (result.error) {
        throw new Error(result.error.message || t('resetPassword.errorReset'))
      }
      return result
    },
    onSuccess: () => {
      setSuccess(true)
      setTimeout(() => navigate({ to: "/login" }), 2000)
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : t('resetPassword.errorReset'))
    },
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError("")
    setSuccess(false)
    if (form.password !== form.confirmPassword) {
      setError(t('resetPassword.errorPasswordMatch'))
      return
    }
    if (!token) {
      setError(t('resetPassword.errorInvalidToken'))
      return
    }
    resetPasswordMutation.mutate({ token, password: form.password })
  }

  return (
    <div>
      <h2 className="mb-6 text-center text-2xl font-bold text-foreground">{t('resetPassword.title')}</h2>
      {error && <div className="mb-4 text-red-500 text-center">{error}</div>}
      {success && <div className="mb-4 text-green-600 text-center">{t('resetPassword.successMessage')}</div>}
      <form className="space-y-6" onSubmit={handleSubmit}>
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-foreground">{t('resetPassword.newPassword')}</label>
          <Input id="password" name="password" type="password" required value={form.password} onChange={handleChange} className="h-10 mt-1 block w-full rounded-md border border-input bg-background text-foreground shadow-sm focus:border-primary focus:ring-primary" />
        </div>
        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-foreground">{t('resetPassword.confirmPassword')}</label>
          <Input id="confirmPassword" name="confirmPassword" type="password" required value={form.confirmPassword} onChange={handleChange} className="h-10 mt-1 block w-full rounded-md border border-input bg-background text-foreground shadow-sm focus:border-primary focus:ring-primary" />
        </div>
        <div>
          <Button type="submit" className="flex w-full justify-center bg-primary text-primary-foreground hover:bg-primary/90" disabled={resetPasswordMutation.isPending}>
            {resetPasswordMutation.isPending ? t('resetPassword.resetting') : t('resetPassword.resetButton')}
          </Button>
        </div>
      </form>
    </div>
  )
}
